import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Icon } from 'antd';
import { ColumnProps, Table } from '@digital/ui';

import { dadosGeraisState, ordemCompraIdState, reloadDadosGeraisState, reloadVendorListState } from '../atoms';
import { useRecoilValue } from 'recoil';
import { isValidGuid } from '@app/helpers/geradorGuid';
import { ISasPropostaOrdemCompra, getPropostasOrdemCompra } from '@app/api/sas/sasProposta';
import { formatarDataPadrao } from '@app/helpers/dateTimeFormats';
import { Visibility } from '@app/components';
import { EGuidStatusSasProposta } from '@app/enums/EGuidStatusSasProposta';
import { isObjNotEmpty } from '@app/helpers/objHelper';
import { currencyFormat } from '@app/helpers/numbers';
import { hasPermission } from '@app/helpers/permissions';
import { getLoggedId } from '@app/helpers';


interface Props { }

const WidgetVendorsInfo = ({ }: Props) => {
    const ordemCompraId = useRecoilValue(ordemCompraIdState);
    const dadosGerais = useRecoilValue(dadosGeraisState);
    const reloadVendorList = useRecoilValue(reloadVendorListState);
    const reloadDadosGerais = useRecoilValue(reloadDadosGeraisState);

    const [vendorList, setVendorList] = useState([]);
    const [statusCodigo, setStatusCodigo] = useState(0);
    const [loggedId] = useState(getLoggedId() || "");
    const [ehComprador, setEhComprador] = useState(false);

    useEffect(() => {
        if (isValidGuid(ordemCompraId)) {
            getPropostasOrdemCompra({ ordemCompraId: ordemCompraId })
                .then((result: any) => {
                    if (result) {
                        setVendorList(result);
                    }
                });
        }
    }, [ordemCompraId, reloadVendorList, reloadDadosGerais]);

    useEffect(() => {
        if (isValidGuid(ordemCompraId) && isObjNotEmpty(dadosGerais)) {
            setEhComprador(dadosGerais.compradorId == loggedId);
            setStatusCodigo(dadosGerais.statusCodigo);
        }
    }, [dadosGerais]);

    const columns = useMemo(() => {
        const columns: ColumnProps<ISasPropostaOrdemCompra>[] = ([] = new Array());

        columns.push({
            title: 'código',
            dataIndex: 'codigoTotvs',
        });

        columns.push({
            title: 'fornecedor',
            dataIndex: 'sasFornecedorOcNome',
            width: '30%'
        });

        columns.push({
            title: 'status',
            dataIndex: 'statusSasPropostaNome',
        });

        columns.push({
            title: 'carta convite',
            dataIndex: 'statusCartaConvite',
            align: 'center',
            render: (text, record) => {
                return (
                    <>
                        <span>{record.statusCartaConvite}</span>
                        <br></br>
                        <span>{formatarDataPadrao(record.dataStatusCartaConvite, true)}</span>
                    </>
                );
            },
        });

        columns.push({
            title: 'Visita Técnica',
            dataIndex: 'statusVisitaTecnica',
            align: 'center',
            render: (text, record) => {
                return (
                    <>
                        <span>{record.statusVisitaTecnica}</span>
                        <br></br>
                        {record.dataStatusVisitaTecnica && formatarDataPadrao(record.dataStatusVisitaTecnica, true)}
                    </>
                );
            },
        });

        columns.push({
            title: 'Esclarecimentos técnicos',
            align: 'center',
            render: (text, record) => (
                <>
                    <span>{record.statusSolicitacaoEsclarecimentosTecnicos}</span>
                    <br></br>
                    {record.dataStatusSolicitacaoEsclarecimentosTecnicos && formatarDataPadrao(record.dataStatusSolicitacaoEsclarecimentosTecnicos, true)}
                </>
            ),
        });

        columns.push({
            title: 'proposta técnica',
            dataIndex: 'dataStatusPropostaTecnica',
            align: 'center',
            render: (text, record) =>
                record.dataStatusPropostaTecnica ? (
                    <>
                        <span>{record.statusPropostaTecnica}</span>
                        <br></br>
                        {formatarDataPadrao(record.dataStatusPropostaTecnica, true)}
                    </>
                ) : (
                    statusCodigo >= 5 && record.statusCartaConvite && !record.statusCartaConvite.toLowerCase().includes("declinou") && <span>Não Enviada</span>
                ),
        });

        columns.push({
            title: "check da documentação",
            dataIndex: 'dataStatusResultadoAvaliacaoTecnica',
            align: 'center',
            render: (text, record) => {
                return (
                    <>
                        <span>{record.statusResultadoAvaliacaoTecnica}</span>
                        <br></br>
                        {formatarDataPadrao(record.dataStatusResultadoAvaliacaoTecnica, true)}
                    </>
                );
            },
        });

        columns.push({
            title: 'envio proposta técnica',
            dataIndex: 'dataStatusEnvioPropostaTecnica',
            align: 'center',
            render: (text, record) =>
                record.dataStatusEnvioPropostaTecnica ? (
                    <>
                        <span>{record.statusEnvioPropostaTecnica}</span>
                        <br></br>
                        {formatarDataPadrao(record.dataStatusEnvioPropostaTecnica, true)}
                    </>
                ) : (
                    statusCodigo >= 5 && record.statusCartaConvite && !record.statusCartaConvite.toLowerCase().includes("declinou") && <span>Não Enviada</span>
                ),
        });

        columns.push({
            title: "parecer técnico",
            dataIndex: 'parecerTecnico',
            align: 'center',
            render: (text, record) => {
                return (
                    <Visibility visible={isObjNotEmpty(record.statusResultadoParecerTecnico)} >
                        <span>{record.statusResultadoParecerTecnico}</span>
                        <br></br>
                        {formatarDataPadrao(record.dataResultadoParecerTecnico, true)}
                    </Visibility>
                );
            }
        });

        columns.push({
            title: "Envio DDI",
            dataIndex: 'statusResultadoEnvioDdi',
            align: 'center',
            render: (text, record) => {
                if (isObjNotEmpty(record.dataResultadoEnvioDdi)) {

                    switch (record.statusResultadoEnvioDdi) {
                        case "Enviado":
                            return (
                                <>
                                    <span>{record.statusResultadoEnvioDdi}</span>
                                    <br></br>
                                    {formatarDataPadrao(record.dataResultadoEnvioDdi)}
                                </>
                            );
                        case "Não Enviado":
                            return (<span>{record.statusResultadoEnvioDdi}</span>);
                        case "Vigente":
                            return (
                                <>
                                    <span>{record.statusResultadoEnvioDdi} </span>
                                    <br></br>
                                    {record.dataValidacao}
                                </>
                            );
                        case "Não Aplicável":
                            return (
                                <>
                                    <span>{record.statusResultadoEnvioDdi}</span>
                                    <br></br>
                                    {formatarDataPadrao(record.dataResultadoEnvioDdi)}
                                </>
                            );
                        default:
                            return (<> </>);
                    }
                }
            }
        });

        columns.push({
            title: "Envio para o DPC",
            align: 'center',
            render: (text, record) => (
                <Visibility
                    visible={record.statusSasPropostaId == EGuidStatusSasProposta.Ativa}
                >
                    <span>{record.resultadoEnvioDpc}</span>
                    <br></br>
                    {["Não Enviado"].includes(record.resultadoEnvioDpc) == false && formatarDataPadrao(record.dataResultadoEnvioDpc, true)}
                </Visibility>
            )
        });

        columns.push({
            title: "Status do Parecer do DPC",
            align: 'center',
            render: (text, record) => (
                <Visibility
                    visible={record.statusSasPropostaId == EGuidStatusSasProposta.Ativa}
                >
                    <span>{record.statusParecerDPC}</span>
                    <br></br>
                    {["Pendente"].includes(record.statusParecerDPC) == false && formatarDataPadrao(record.dataStatusParecerDPC, true)}
                </Visibility>
            )
        });

        columns.push({
            title: "Resultado do Parecer do DPC",
            // dataIndex: 'resultadoParecerDPC',
            align: 'center',
            render: (text, record) => (
                <Visibility
                    visible={record.statusSasPropostaId == EGuidStatusSasProposta.Ativa}
                >
                    <span>{record.resultadoParecerDPC}</span>
                    <br></br>
                    {formatarDataPadrao(record.dataResultadoParecerDPC, true)}
                </Visibility>
            )
        });

        columns.push({
            title: "Status Avaliação Financeira (Painel)",
            dataIndex: 'statusResultadoEnvioConfide',
            align: 'center',
            render: (text, record) =>
                record.statusResultadoEnvioConfide ? (
                    <>
                        <span>{record.statusResultadoEnvioConfide}</span>
                        <br></br>
                        {formatarDataPadrao(record.dataResultadoEnvioConfide, true)}
                    </>
                ) : (
                    statusCodigo >= 19 && <span>Não Enviada</span>
                ),
        });

        columns.push({
            title: "Relatório Financeiro",
            dataIndex: 'statusRelatorioConfide',
            align: 'center',
            render: (text, record) =>
                record.statusRelatorioConfide ? (
                    <>
                        <span>{record.statusRelatorioConfide}</span>
                        <br></br>
                        {record.statusRelatorioConfide === "Não Aplicável" ?
                            formatarDataPadrao(record.dataStatusRelatorioConfide, true) : <></>}
                    </>
                ) : (
                    statusCodigo >= 19 && <span>Não Enviada</span>
                ),
        });

        columns.push({
            title: "Avaliação Financeira",
            dataIndex: 'statusSolicitacaoAvaliacaoFinanceira',
            align: 'center',
            render: (text, record) =>
                record.statusSolicitacaoAvaliacaoFinanceira ? (
                    <>
                        <span>{record.statusSolicitacaoAvaliacaoFinanceira}</span>
                        <br></br>
                        {record.statusSolicitacaoAvaliacaoFinanceira === "Não Aplicável" ?
                            formatarDataPadrao(record.dataStatusSolicitacaoAvaliacaoFinanceira, true) : <></>}
                    </>
                ) : (
                    statusCodigo >= 19 && <span>Não Enviada</span>
                ),
        });

        columns.push({
            title: "Status da Avaliação Financeira",
            dataIndex: 'statusCadastroResultadoAvaliacaoFinanceira',
            align: 'center',
            render: (text, record) =>
                record.statusCadastroResultadoAvaliacaoFinanceira ? (
                    <>
                        <span>{record.statusCadastroResultadoAvaliacaoFinanceira}</span>
                        <br></br>
                        {formatarDataPadrao(record.dataStatusCadastroResultadoAvaliacaoFinanceira, true)}
                    </>
                ) : (
                    statusCodigo >= 19 && <span>Não Enviada</span>
                ),
        });

        columns.push({
            title: "Resultado da Avaliação Financeira",
            dataIndex: 'resultadoCadastroResultadoAvaliacaoFinanceira',
            align: 'center',
            render: (text, record) =>
                record.resultadoCadastroResultadoAvaliacaoFinanceira ? (
                    <>
                        <span>{record.resultadoCadastroResultadoAvaliacaoFinanceira}</span>
                        <br></br>
                        {record.resultadoCadastroResultadoAvaliacaoFinanceira === "Não Aplicável" ?
                            formatarDataPadrao(record.dataStatusCadastroResultadoAvaliacaoFinanceira, true) : <></>}
                    </>
                ) : (
                    statusCodigo >= 19 && <span>Não Enviada</span>
                ),
        });

        columns.push({
            title: 'proposta comercial',
            dataIndex: 'dataRecebimentoPropostaComercial',
            align: 'center',
            render: (text, record) => (
                <Visibility visible={record.statusSasPropostaId == EGuidStatusSasProposta.Ativa}>
                    {
                        record.dataRecebimentoPropostaComercial ? (
                            <>
                                <span>{record.statusPropostaComercial ? record.statusPropostaComercial : ""}</span>
                                <br></br>
                                {formatarDataPadrao(record.dataRecebimentoPropostaComercial, true)}
                            </>
                        ) : (
                            statusCodigo >= 19 && <span>Pendente</span>
                        )
                    }
                </Visibility>
            )
        });

        columns.push({
            title: 'avaliação comercial',
            dataIndex: 'dataResultadoAvaliacaoComercial',
            align: 'center',
            render: (text, record) => {
                return (
                    <>
                        <span>{record.statusResultadoAvaliacaoComercial}</span>
                        <br></br>
                        {formatarDataPadrao(record.dataStatusResultadoAvaliacaoComercial, true)}
                    </>
                );
            },
        });

        columns.push({
            title: "Status da Negociação da Minuta",
            dataIndex: 'statusEnvioNegociacaoMinutaContratual',
            align: 'center',
            render: (text, record) => (
                <>
                    <span>{record.statusEnvioNegociacaoMinutaContratual}</span>
                    <br></br>
                    {
                        record.dataValidacaoEnvioNegociacaoMinutaContratual != null ?
                            formatarDataPadrao(record.dataValidacaoEnvioNegociacaoMinutaContratual, true)
                            :
                            record.dataStatusEnvioNegociacaoMinutaContratual != null ?
                                formatarDataPadrao(record.dataStatusEnvioNegociacaoMinutaContratual, true)
                                : ''
                    }
                </>
            ),
        });

        columns.push({
            title: "Status da Chancela",
            dataIndex: 'statusConfirmarSolicitacaoChancelaGJMinuta',
            align: 'center',
            render: (text, record) => (
                <>
                    <span>{record.statusConfirmarSolicitacaoChancelaGJMinuta}</span>
                    <br></br>
                    {record.statusConfirmarSolicitacaoChancelaGJMinuta != "Chancelado"
                        && record.dataStatusConfirmarSolicitacaoChancelaGJMinuta
                        && formatarDataPadrao(record.dataStatusConfirmarSolicitacaoChancelaGJMinuta, true)}
                    {record.dataRegistroChancelaSolicitacaoChancelaGJMinuta && formatarDataPadrao(record.dataRegistroChancelaSolicitacaoChancelaGJMinuta, true)}
                </>
            ),
        });

        columns.push({
            title: "Status do CES",
            dataIndex: 'statusAprovarCES',
            align: 'center',
            render: (text, record) => {
                return (
                    <Visibility visible={record.statusSasPropostaId == EGuidStatusSasProposta.Ativa}>
                        <span>{record.statusAprovarCES}</span>
                        <br></br>
                        {record.statusAprovarCES && record.statusAprovarCES != "" && record.statusAprovarCES !== "Pendente de Envio" && formatarDataPadrao(record.dataStatusAprovarCES, true)}
                    </Visibility>
                );
            },
        });

        if (hasPermission(['SAS_VISUALIZAR_VALOR_PROPOSTA_COMERCIAL']) || ehComprador) {
            columns.push({
                title: "Ranking",
                dataIndex: 'ranking',
                align: 'center',
                render: (text, record) => (
                    //<Visibility visible={record.ranking != 0 && currencyFormat(record.valorPropostaComercial) != "R$ 0,00"}>
                    <Visibility visible={record.ranking != 0}>
                        <span>{record.ranking}</span>
                    </Visibility>
                ),
            });

            columns.push({
                title: 'valor',
                dataIndex: 'valorPropostaComercial',
                align: 'center',
                render: (text, record) => {
                    return (
                        <>{currencyFormat(record.valorPropostaComercial)} </>
                    );
                }
            });
        }

        return columns;
    }, [vendorList]);

    return (
        <Collapse
            bordered={false}
            defaultActiveKey="vendorsInfo"
            expandIcon={() => <Icon type="arrows-right_4" />}>
            <Collapse.Panel
                key="vendorsInfo"
                header="Vendors Info">
                <Table
                    className='tabela-vendor-list-info'
                    columns={columns}
                    dataSource={vendorList}
                    rowKey={'id'}
                    pagination={false}
                    rowClassName={(record: any) => (record.numeroVencedor ? 'row-vencedor' : '')}
                    style={{ marginLeft: 4, marginRight: 4, marginBottom: 10 }}
                />
            </Collapse.Panel>
        </Collapse>
    );
};

export default WidgetVendorsInfo;